import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface DMCampaign {
  id: string;
  creatorId: string;
  name: string;
  subject: string;
  message: string;
  targetTiers?: string[];
  targetTags?: string[];
  scheduledFor?: Date;
  sentAt?: Date;
  status: 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'FAILED';
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateDMCampaignData {
  name: string;
  subject: string;
  message: string;
  targetTiers?: string[];
  targetTags?: string[];
  scheduledFor?: string;
}

export interface DMCampaignStats {
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  openRate?: number;
  clickRate?: number;
}

const dmCampaignService = {
  // Create campaign
  create: async (data: CreateDMCampaignData): Promise<DMCampaign> => {
    const response = await axios.post(`${API_URL}/api/dm-campaigns`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get all campaigns
  getAll: async (params?: {
    status?: string;
  }): Promise<DMCampaign[]> => {
    const response = await axios.get(`${API_URL}/api/dm-campaigns`, {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get single campaign
  getById: async (id: string): Promise<DMCampaign> => {
    const response = await axios.get(`${API_URL}/api/dm-campaigns/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get campaign stats
  getStats: async (id: string): Promise<DMCampaignStats> => {
    const response = await axios.get(`${API_URL}/api/dm-campaigns/${id}/stats`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Send campaign
  send: async (id: string): Promise<DMCampaign> => {
    const response = await axios.post(
      `${API_URL}/api/dm-campaigns/${id}/send`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Preview campaign recipients
  previewRecipients: async (data: {
    targetTiers?: string[];
    targetTags?: string[];
  }): Promise<{ count: number; preview: any[] }> => {
    const response = await axios.post(
      `${API_URL}/api/dm-campaigns/preview-recipients`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Delete campaign
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_URL}/api/dm-campaigns/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  },
};

export default dmCampaignService;
